<template>
  <div class="batch-financial-tab">
    <div class="loading loading-lg cr-panel-loading" v-if="loading"></div>
    <template v-else>
      <div class="cr-box">
        <div class="cr-box-title">
          <fa-icon :icon="['fal', 'hand-holding-usd']"/>Título a receber
          <span class="chip" v-if="receivable && receivable.document">
            {{ receivable.document }}
          </span>
        </div>
        <template v-if="receivable">
          <div class="fin-row fin-row-head">
            <span>Entidade</span>
            <span class="n">Valor original</span>
            <span class="n">Valor atual</span>
            <span class="n">Vencimento</span>
            <span class="n">Competência</span>
            <span class="n">Status</span>
            <span></span>
          </div>
          <div class="fin-row">
            <span class="text-bold fin-row-label">
              {{ receivable.entity ? receivable.entity.name : '—' }}
            </span>
            <span class="n num text-gray">{{ valueOf(receivable, 'total') | currency }}</span>
            <span class="n num text-bold">
              {{ valueOf(receivable, 'current') | currency }}
              <span class="sub" v-if="adjusted(receivable)">ajustado pela análise</span>
            </span>
            <span class="n num">{{ receivable.dueDate | date }}</span>
            <span class="n num">{{ receivable.competenceDate | date('MM/YYYY', true) }}</span>
            <span class="n">
              <span class="pill" :class="statusClass(receivable)">
                {{ statusName(receivable) }}
              </span>
            </span>
            <span class="fin-actions">
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Abrir título" @click="$emit('open-financial', receivable)">
                <fa-icon :icon="['fal', 'external-link']"/>
              </button>
            </span>
          </div>
          <div class="fin-row" v-for="recovery in recoveries" :key="recovery.id">
            <span class="text-bold fin-row-label">
              Recuperação de glosa
              <span class="sub">{{ recovery.document }}</span>
            </span>
            <span class="n num text-gray">—</span>
            <span class="n num text-bold">{{ valueOf(recovery, 'current') | currency }}</span>
            <span class="n num">{{ recovery.dueDate | date }}</span>
            <span class="n num">{{ recovery.competenceDate | date('MM/YYYY', true) }}</span>
            <span class="n">
              <span class="pill" :class="statusClass(recovery)">{{ statusName(recovery) }}</span>
            </span>
            <span class="fin-actions">
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Abrir título" @click="$emit('open-financial', recovery)">
                <fa-icon :icon="['fal', 'external-link']"/>
              </button>
            </span>
          </div>
        </template>
        <div class="text-gray text-small" v-else>
          Nenhum título a receber vinculado a este lote.
        </div>
      </div>

      <div class="cr-box">
        <div class="cr-box-title">
          <fa-icon :icon="['fal', 'receipt']"/>Repasses por profissional
        </div>
        <template v-if="transfers.length > 0">
          <div class="fin-row fin-row-head">
            <span>Profissional</span>
            <span class="n">Bruto</span>
            <span class="n">Glosa abatida</span>
            <span class="n">Recuperado</span>
            <span class="n">A pagar</span>
            <span class="n">Status</span>
            <span></span>
          </div>
          <div class="fin-row" v-for="transfer in transfers" :key="transfer.id">
            <span class="text-bold fin-row-label">
              {{ transfer.name || (transfer.entity && transfer.entity.name) || '—' }}
              <span class="sub">
                {{ transfer.status === 'paid' && transfer.paidAt
                  ? `pago em ${dateFilter(transfer.paidAt)}`
                  : `vence ${dateFilter(transfer.dueDate)}` }}
              </span>
              <span class="sub text-warning"
                    v-if="transfer.status === 'paid' && transfer.glossed > 0">
                Glosa de {{ transfer.glossed | currency }} vira ajuste no próximo repasse
              </span>
            </span>
            <span class="n num">{{ transfer.gross | currency }}</span>
            <span class="n num" :class="{'text-error': transfer.glossed > 0}">
              {{ transfer.glossed | currency }}
            </span>
            <span class="n num">{{ transfer.recovered | currency }}</span>
            <span class="n num text-bold">{{ valueOf(transfer, 'current') | currency }}</span>
            <span class="n">
              <span class="pill" :class="statusClass(transfer)">{{ statusName(transfer) }}</span>
            </span>
            <span class="fin-actions">
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Recibo"
                      :class="{loading: printingId === transfer.bundleId}"
                      :disabled="!!printingId"
                      @click="$emit('print-receipt', transfer)">
                <fa-icon :icon="['fal', 'receipt']"/>
              </button>
              <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                      data-tooltip="Abrir" @click="$emit('open-financial', transfer)">
                <fa-icon :icon="['fal', 'external-link']"/>
              </button>
            </span>
          </div>
        </template>
        <div class="text-gray text-small" v-else>
          Nenhum repasse gerado para este lote.
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import dateFilter from '@/filters/date';

const STATUS_NAMES = {
  open: 'Pendente',
  paid: 'Pago',
  canceled: 'Cancelado',
};

export default {
  props: {
    receivable: {
      type: Object,
      default: null,
    },
    recoveries: {
      type: Array,
      default: () => [],
    },
    transfers: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    printingId: {
      type: String,
      default: null,
    },
  },
  methods: {
    dateFilter,
    valueOf(financial, field) {
      const value = (financial && financial.value) || {};
      if (typeof value === 'number') return value;
      return value[field] || 0;
    },
    adjusted(financial) {
      return this.valueOf(financial, 'total') !== this.valueOf(financial, 'current');
    },
    statusName(financial) {
      if (financial.type === 'revenue' && financial.status === 'paid') return 'Recebido';
      return STATUS_NAMES[financial.status] || financial.status;
    },
    statusClass(financial) {
      if (financial.status === 'paid') return 'pill-ready';
      if (financial.status === 'canceled') return 'pill-error';
      return '';
    },
  },
};
</script>
